// BetBuilderIntlPage — the dedicated WC Bet Builder product, built in the same
// SIGNALS design language as Rolls (intl-signals.css). One 3D tilt-card per
// fixture leads with the combined-odds hero + the two nations; clicking lifts
// the card into a focus overlay holding every prop leg and a stake ticket.
// A "full builder" slip carries player props; a "match props" slip is the
// pre-lineup form (corners/cards/fouls/timing).

import "../../styles/intl-signals.css";
import { useEffect, useRef, useState, type JSX, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { NationFlag, IIcon } from "../international/_intlkit.tsx";
import type { IntlAccaTournament } from "../../db/intl-acca-types";
import { formatOdds } from "../../db/intl-acca-types";
import type { BetBuilderSlip, BetBuilderLeg } from "../../intl-acca/useIntlBetBuilder.ts";

export interface BetBuilderIntlPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slips: BetBuilderSlip[];
}

const FAMILY_LABEL: Record<string, string> = {
  corners: "Corners", cards: "Cards", fouls: "Fouls", shots: "Shots", timing: "Timing",
  player_goals: "Goals", player_shots: "Shots", player_cards: "Cards",
  player_fouls: "Fouls", player_tackles: "Tackles", player_saves: "Saves",
};

function kickoffLabel(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function useCountUp(target: number): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current, to = target;
    if (from === to) { setVal(to); return; }
    const dur = Math.abs(to) >= 1e5 ? 850 : Math.abs(to) >= 1e3 ? 600 : 460;
    const t0 = performance.now();
    const ease = (p: number) => 1 - Math.pow(1 - p, 3);
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      setVal(from + (to - from) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else { fromRef.current = to; setVal(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [target]);
  return val;
}

function StateWrap({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="isig-state">
      <div className="isig-state-title">{title}</div>
      <p className="isig-state-body">{body}</p>
    </div>
  );
}

function SlipCard({ slip, onOpen }: { slip: BetBuilderSlip; onOpen: () => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const odds = slip.combined_odds ?? 0;
  const edge = Math.min(1, Math.max(0.05, Math.log10(Math.max(odds, 1.01)) / 2));
  const probs = slip.legs.map((l) => l.fair_prob).filter((n): n is number => n != null);
  const avgProb = probs.length ? probs.reduce((a, b) => a + b, 0) / probs.length : null;
  const families = [...new Set(slip.legs.map((l) => l.family).filter(Boolean) as string[])];

  const onMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--ry", `${(px * 9).toFixed(2)}deg`);
    el.style.setProperty("--rx", `${(-py * 9).toFixed(2)}deg`);
    el.style.setProperty("--mx", `${((px + 0.5) * 100).toFixed(1)}%`);
    el.style.setProperty("--my", `${((py + 0.5) * 100).toFixed(1)}%`);
  };
  const onEnter = (): void => { ref.current?.style.setProperty("--lift", "-9px"); };
  const onLeave = (): void => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg"); el.style.setProperty("--ry", "0deg"); el.style.setProperty("--lift", "0px");
  };

  return (
    <div
      ref={ref} className="isig-card isig-card--clickable" style={{ "--edge": edge } as CSSProperties}
      onMouseMove={onMove} onMouseEnter={onEnter} onMouseLeave={onLeave}
      onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <div className="isig-top">
        <span className="isig-time">{kickoffLabel(slip.kickoff)}</span>
        <span className="isig-tier">{slip.leg_count} legs</span>
      </div>
      <div className="isig-roll-id">
        <span className="isig-roll-tier">{slip.fixture_label ?? slip.match_id}</span>
        <span className="isig-roll-sub">{slip.slip_kind === "full_builder" ? "Full builder · player props" : "Match props"}</span>
      </div>
      <div className="isig-flagstrip">
        {slip.home_code && <NationFlag code={slip.home_code} size={18} />}
        {slip.away_code && <NationFlag code={slip.away_code} size={18} />}
      </div>
      <div className="isig-edge">
        <div className="isig-clv-row">
          <span className="isig-clv gold">×{odds.toFixed(2)}</span>
          <span className="isig-clv-label">Combined odds</span>
        </div>
        {avgProb != null && <div className="isig-bar gold"><span style={{ width: `${Math.min(100, Math.max(6, avgProb * 100))}%` }} /></div>}
      </div>
      <div className="isig-more">
        <span>{families.slice(0, 4).map((f) => FAMILY_LABEL[f] ?? f).join(" · ")}</span>
        <IIcon name="chart" size={13} />
      </div>
    </div>
  );
}

function SlipTicket({ slip, stake, setStake }: { slip: BetBuilderSlip; stake: number; setStake: (n: number) => void }): JSX.Element {
  const odds = slip.combined_odds ?? 0;
  const winnings = useCountUp(stake * odds);
  const animOdds = useCountUp(odds);
  const chips = [10, 25, 50, 100];
  const oddsList = slip.legs.map((l) => l.fair_odds).filter((n): n is number => Number.isFinite(n as number));
  const longest = oddsList.length ? Math.max(...oddsList) : 0;
  const shortest = oddsList.length ? Math.min(...oddsList) : 0;
  const probs = slip.legs.map((l) => l.fair_prob).filter((n): n is number => n != null);
  const avgProb = probs.length ? Math.round((probs.reduce((a, b) => a + b, 0) / probs.length) * 100) : null;
  return (
    <div className="isig-tkt">
      <div className="isig-tkt-row">
        <div className="isig-tkt-block">
          <span className="isig-sec-lbl">Combined odds</span>
          <span className="isig-tkt-odds">×{animOdds.toFixed(2)}</span>
        </div>
        <div className="isig-tkt-block">
          <span className="isig-sec-lbl">Your stake</span>
          <div className="isig-tkt-inp">
            <span className="pfx">$</span>
            <input className="isig-tkt-input" type="number" min="0" inputMode="decimal"
              value={stake} onChange={(e) => setStake(Math.max(0, Number(e.target.value) || 0))} />
          </div>
          <div className="isig-tkt-chips">
            {chips.map((c) => <button key={c} className={`isig-tkt-chip ${stake === c ? "on" : ""}`} onClick={() => setStake(c)}>${c}</button>)}
          </div>
        </div>
      </div>
      <div className="isig-tkt-win">
        <span className="isig-sec-lbl">Potential return</span>
        <span className="isig-tkt-win-val">${Math.round(winnings).toLocaleString("en-US")}</span>
      </div>
      <div className="isig-tkt-meta">
        <div className="m"><span className="k">Legs</span><span className="val">{slip.leg_count}</span></div>
        {avgProb != null && <div className="m"><span className="k">Avg model</span><span className="val gold">{avgProb}%</span></div>}
        <div className="m"><span className="k">Longest</span><span className="val">{formatOdds(longest)}</span></div>
        <div className="m"><span className="k">Shortest</span><span className="val">{formatOdds(shortest)}</span></div>
      </div>
    </div>
  );
}

function SlipOverlay({ slip, stake, setStake, onClose }: {
  slip: BetBuilderSlip; stake: number; setStake: (n: number) => void; onClose: () => void;
}): JSX.Element {
  const odds = slip.combined_odds ?? 0;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return (
    <div className="isig-overlay" onClick={onClose}>
      <div className="isig-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="isig-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="isig-modal-head">
          <div className="isig-top">
            <span className="isig-time">{kickoffLabel(slip.kickoff)}</span>
            <span className="isig-tier">{slip.slip_kind === "full_builder" ? "Full builder" : "Match props"} · {slip.leg_count} legs</span>
          </div>
          <div className="isig-roll-id">
            <span className="isig-roll-tier">{slip.fixture_label ?? slip.match_id}</span>
            <span className="isig-roll-sub">Single-match bet builder</span>
          </div>
          <div className="isig-edge">
            <div className="isig-clv-row">
              <span className="isig-clv gold">×{odds.toFixed(2)}</span>
              <span className="isig-clv-label">Combined odds</span>
            </div>
          </div>
        </div>

        <div className="isig-sec">
          <p className="isig-sec-lbl">The legs</p>
          <div className="isig-rl-legs">
            {slip.legs.map((l: BetBuilderLeg, i: number) => {
              const prob = l.fair_prob != null ? Math.round(l.fair_prob * 100) : null;
              return (
                <div className="isig-rl-leg" key={l.id}>
                  <span className="isig-rl-rank">{String(i + 1).padStart(2, "0")}</span>
                  <div className="isig-rl-main">
                    <div className="isig-rl-pick">
                      <span className="pred">{l.market_label ?? l.selection ?? l.market_code}</span>
                      {prob != null && <span className="prob">{prob}% model</span>}
                    </div>
                    {l.family && <div className="isig-rl-fix"><span>{FAMILY_LABEL[l.family] ?? l.family}</span></div>}
                  </div>
                  <span className="isig-rl-odds">{l.fair_odds != null ? formatOdds(l.fair_odds) : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>

        {slip.swarm_note && (
          <>
            <div className="isig-divider" />
            <div className="isig-sec">
              <p className="isig-sec-lbl">The swarm's read</p>
              <p style={{ fontSize: 13, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>{slip.swarm_note}</p>
            </div>
          </>
        )}

        <div className="isig-divider" />

        <div className="isig-sec">
          <p className="isig-sec-lbl">The ticket</p>
          <SlipTicket slip={slip} stake={stake} setStake={setStake} />
        </div>
        <p className="isig-tkt-foot">Composed by the analyst swarm · model fair prices · read-only intelligence · no bet placement</p>
      </div>
    </div>
  );
}

export function BetBuilderIntlPage(props: BetBuilderIntlPageProps): JSX.Element {
  const { loading, error, tournament, slips } = props;
  const [open, setOpen] = useState<BetBuilderSlip | null>(null);
  const [stake, setStake] = useState(25);

  if (loading) return <StateWrap title="Loading the builders…" body="Pulling the agent-composed builders from the desk." />;
  if (error) return <StateWrap title="Bet Builder unavailable" body={error} />;
  if (slips.length === 0) {
    return <StateWrap title="No agent builders live yet" body="Every builder here is composed by the analyst swarm — and the swarm only ships a builder it actually backs. Right now it's holding: on thin pre-tournament data the agents back too few legs to assemble one. Genuine agent builders populate as played-match data and confirmed line-ups land. Empty by design — no fabricated picks." />;
  }

  return (
    <div>
      <div className="isig-rolls-head">
        <div className="isig-eyebrow">International · Agent Swarm{tournament ? ` · ${tournament.name}` : ""}</div>
        <h1 className="isig-title">Bet Builder</h1>
        <p className="isig-count">
          {slips.length} agent-composed builder{slips.length === 1 ? "" : "s"} · every leg backed by the analyst swarm · click any fixture for the legs &amp; the desk's read.
        </p>
      </div>
      <div className="isig-grid">
        {slips.map((s) => <SlipCard key={s.id} slip={s} onOpen={() => setOpen(s)} />)}
      </div>
      {open && <SlipOverlay slip={open} stake={stake} setStake={setStake} onClose={() => setOpen(null)} />}
    </div>
  );
}
